import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import "./customizeColours.css";

const MAX_QUESTIONS = 50;
const PAGE_SIZE = 5;
const MIN_ANSWERS = 1;
const MAX_ANSWERS = 3;
const TEXT_MAX = 500;
const WHY_MAX = 600;

const isTrue = (v) => String(v ?? "").trim().toLowerCase() === "true";

function normalizeQuestions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, MAX_QUESTIONS).map((q, i) => {
    const answers = Array.isArray(q?.correct_answers)
      ? q.correct_answers.map((a) => String(a).trim()).filter(Boolean).slice(0, MAX_ANSWERS)
      : [];
    const selectCount = Math.min(
      MAX_ANSWERS,
      Math.max(MIN_ANSWERS, Number(q?.select_count) || answers.length || MIN_ANSWERS)
    );
    return {
      id: String(q?.id || `q${i + 1}`),
      text: String(q?.text || "").trim(),
      select_count: answers.length || selectCount,
      correct_answers: answers,
      why_these_answers_fit: String(q?.why_these_answers_fit || "").trim(),
    };
  });
}

function nextId(list) {
  const nums = list
    .map((q) => Number(String(q.id).replace(/\D/g, "")))
    .filter((n) => Number.isFinite(n) && n > 0);
  return `q${(nums.length ? Math.max(...nums) : 0) + 1}`;
}

export default function QuestionsEditor({ data, currentTheme, adminToken, colors = [] }) {
  const dispatch = useDispatch();
  const [questions, setQuestions] = useState([]);
  const [showWhy, setShowWhy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggleSaving, setToggleSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [text, setText] = useState("");
  const [whyText, setWhyText] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [page, setPage] = useState(1);

  const colorValues = colors.map((c) => c.color_value).filter(Boolean);
  const totalPages = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageQuestions = questions.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    if (!data) return;
    if ("questions" in data) setQuestions(normalizeQuestions(data.questions));
    if ("show_why_answers_fit" in data) setShowWhy(isTrue(data.show_why_answers_fit));
  }, [data]);

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(1, Math.ceil(questions.length / PAGE_SIZE) || 1)));
  }, [questions.length]);

  const persist = async (next, msg = "Questions saved.") => {
    if (!currentTheme || !adminToken) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return false;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: { questions: next, total_questions: next.length },
          currentTheme,
        },
        token: adminToken,
      });
      setQuestions(next);
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", msg, "success");
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save questions.", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleToggleShowWhy = async (checked) => {
    if (!currentTheme || !adminToken) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return;
    }
    setShowWhy(checked);
    setToggleSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: { show_why_answers_fit: checked ? "true" : "false" },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
    } catch (e) {
      setShowWhy(!checked);
      Swal.fire("Error", e.message || "Failed to update toggle.", "error");
    } finally {
      setToggleSaving(false);
    }
  };

  const openAdd = () => {
    if (questions.length >= MAX_QUESTIONS) {
      Swal.fire("Limit", `Maximum ${MAX_QUESTIONS} questions allowed.`, "warning");
      return;
    }
    if (!colorValues.length) {
      Swal.fire("Add colours first", "Create at least one colour value before adding questions.", "warning");
      return;
    }
    setText("");
    setWhyText("");
    setSelectedValues([]);
    setModal({ mode: "add" });
  };

  const openEdit = (index) => {
    const q = questions[index];
    setText(q.text);
    setWhyText(q.why_these_answers_fit || "");
    setSelectedValues([...(q.correct_answers || [])]);
    setModal({ mode: "edit", index });
  };

  const toggleValue = (value) => {
    setSelectedValues((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= MAX_ANSWERS) {
        Swal.fire("Limit", `Select at most ${MAX_ANSWERS} answers.`, "info");
        return prev;
      }
      return [...prev, value];
    });
  };

  const handleSaveModal = async () => {
    const trimmed = text.trim();
    const why = whyText.trim();
    if (!trimmed) {
      Swal.fire("Required", "Question text is required.", "warning");
      return;
    }
    if (trimmed.length > TEXT_MAX) {
      Swal.fire("Too long", `Question max ${TEXT_MAX} characters.`, "warning");
      return;
    }
    if (!why) {
      Swal.fire("Required", "“Why these answers fit” is required.", "warning");
      return;
    }
    if (why.length > WHY_MAX) {
      Swal.fire("Too long", `Explanation max ${WHY_MAX} characters.`, "warning");
      return;
    }
    if (selectedValues.length < MIN_ANSWERS || selectedValues.length > MAX_ANSWERS) {
      Swal.fire(
        "Answers",
        `Select between ${MIN_ANSWERS} and ${MAX_ANSWERS} colour values as answers.`,
        "warning"
      );
      return;
    }
    const invalid = selectedValues.filter((v) => !colorValues.includes(v));
    if (invalid.length) {
      Swal.fire("Invalid", "Some selected answers no longer exist in colours.", "warning");
      return;
    }

    const entry = {
      id: modal.mode === "edit" ? questions[modal.index].id : nextId(questions),
      text: trimmed,
      select_count: selectedValues.length,
      correct_answers: selectedValues,
      why_these_answers_fit: why,
    };

    const next =
      modal.mode === "add"
        ? [...questions, entry]
        : questions.map((q, i) => (i === modal.index ? entry : q));

    const ok = await persist(next, modal.mode === "add" ? "Question added." : "Question updated.");
    if (ok) {
      setModal(null);
      if (modal.mode === "add") {
        setPage(Math.ceil(next.length / PAGE_SIZE) || 1);
      }
    }
  };

  const handleDelete = async (index) => {
    const q = questions[index];
    const confirm = await Swal.fire({
      title: "Delete question?",
      text: q.text.slice(0, 80) + (q.text.length > 80 ? "…" : ""),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;
    await persist(
      questions.filter((_, i) => i !== index),
      "Question deleted."
    );
  };

  return (
    <div className="coc-admin-section" data-tour="coc-questions">
      <div className="coc-admin-header">
        <div>
          <h2 className="coc-admin-title">
            <i className="fa-solid fa-circle-question" /> &nbsp;Situations / Questions
          </h2>
          <p className="coc-admin-subtitle">
            {questions.length} / {MAX_QUESTIONS} · each question has 1–{MAX_ANSWERS} correct colour
            values
          </p>
        </div>
        <div className="catmgr-header-right">
          <button
            type="button"
            className="catmgr-btn catmgr-btn-outline"
            onClick={openAdd}
            disabled={saving || questions.length >= MAX_QUESTIONS}
          >
            <i className="fa-solid fa-plus" /> Add Question
          </button>
        </div>
      </div>

      <div className="coc-why-toggle" data-tour="coc-why-toggle">
        <div>
          <div className="coc-why-toggle-title">Show “Why these answers fit”</div>
          <div className="coc-why-toggle-hint">
            When on, players see an explanation popup after submitting each situation.
          </div>
        </div>
        <div className="coc-why-toggle-control">
          <Switch
            checked={showWhy}
            onChange={handleToggleShowWhy}
            disabled={toggleSaving}
            onColor="#7c3aed"
            offColor="#cbd5e1"
            height={22}
            width={44}
          />
          <span className="coc-why-toggle-label">{showWhy ? "Enabled" : "Disabled"}</span>
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="coc-q-empty">
          No questions yet. Add colours first, then create situations.
        </div>
      ) : (
        <>
          <div className="coc-q-list">
            {pageQuestions.map((q, pageIndex) => {
              const index = pageStart + pageIndex;
              return (
                <article className="coc-q-card" key={q.id}>
                  <div className="coc-q-card-top">
                    <div className="coc-q-card-meta">
                      <span className="coc-q-badge">{index + 1}</span>
                      <span className="coc-q-label">Situation {index + 1}</span>
                      <span className="coc-q-choose">
                        Choose {q.select_count} colour{q.select_count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="coc-q-actions">
                      <button
                        type="button"
                        className="catmgr-icon-btn catmgr-icon-btn-sm"
                        title="Edit"
                        onClick={() => openEdit(index)}
                        disabled={saving}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        type="button"
                        className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                        title="Delete"
                        onClick={() => handleDelete(index)}
                        disabled={saving}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </div>

                  <p className="coc-q-text">{q.text}</p>

                  <div className="coc-q-section">
                    <span className="coc-q-section-label">Correct answers</span>
                    <div className="coc-q-answers">
                      {(q.correct_answers || []).map((a) => {
                        const color = colors.find((c) => c.color_value === a);
                        return (
                          <span className="coc-q-chip" key={a}>
                            <span
                              className="coc-q-chip-dot"
                              style={{ background: color?.color_code || "#94a3b8" }}
                            />
                            {color?.color_name ? (
                              <span className="coc-q-chip-name">{color.color_name}</span>
                            ) : null}
                            {a}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {q.why_these_answers_fit ? (
                    <div className="coc-q-section">
                      <span className="coc-q-section-label">Why these answers fit</span>
                      <div className="coc-q-why">
                        <p className="coc-q-why-text">{q.why_these_answers_fit}</p>
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>

          {totalPages > 1 ? (
            <div className="coc-q-pagination">
              <button
                type="button"
                className="coc-q-page-btn"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </button>
              <span className="coc-q-page-info">
                Page {currentPage} of {totalPages}
                <span className="coc-q-page-range">
                  {" "}
                  · showing {pageStart + 1}–
                  {Math.min(pageStart + PAGE_SIZE, questions.length)} of {questions.length}
                </span>
              </span>
              <button
                type="button"
                className="coc-q-page-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </button>
            </div>
          ) : null}
        </>
      )}

      {modal ? (
        <>
          <div className="catmgr-backdrop" onClick={() => setModal(null)} />
          <div className="catmgr-modal catmgr-modal-wide">
            <h3>{modal.mode === "add" ? "Add Question" : "Edit Question"}</h3>

            <label className="catmgr-modal-label">Situation text</label>
            <textarea
              className="catmgr-modal-input"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, TEXT_MAX))}
              placeholder="Describe the situation…"
              maxLength={TEXT_MAX}
            />
            <p className="quiz-question-text-length">
              {text.length} / {TEXT_MAX}
            </p>

            <label className="catmgr-modal-label">
              Correct colour values ({selectedValues.length} / {MAX_ANSWERS})
            </label>
            <p className="catmgr-modal-hint">
              Select {MIN_ANSWERS}–{MAX_ANSWERS}. Players must pick this many colours.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {colorValues.length === 0 ? (
                <div className="catmgr-empty">No colour values available.</div>
              ) : (
                colorValues.map((value) => {
                  const color = colors.find((c) => c.color_value === value);
                  const on = selectedValues.includes(value);
                  const locked = !on && selectedValues.length >= MAX_ANSWERS;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleValue(value)}
                      disabled={locked}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        textAlign: "left",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: on ? "2px solid #E25569" : "1px solid #ddd",
                        background: on ? "#fff5f6" : "#fff",
                        cursor: locked ? "not-allowed" : "pointer",
                        opacity: locked ? 0.5 : 1,
                      }}
                    >
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: color?.color_code || "#888",
                          border: "1px solid #ccc",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1 }}>
                        <strong>{color?.color_name || "Colour"}</strong> — {value}
                      </span>
                      {on ? <i className="fa-solid fa-check" style={{ color: "#E25569" }} /> : null}
                    </button>
                  );
                })
              )}
            </div>

            <label className="catmgr-modal-label">Why these answers fit</label>
            <textarea
              className="catmgr-modal-input"
              rows={3}
              value={whyText}
              onChange={(e) => setWhyText(e.target.value.slice(0, WHY_MAX))}
              placeholder="Explain why the selected colour values fit this situation…"
              maxLength={WHY_MAX}
            />
            <p className="quiz-question-text-length">
              {whyText.length} / {WHY_MAX}
            </p>

            <div className="catmgr-modal-actions">
              <button type="button" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={handleSaveModal} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
